import { formatDurationRangeLabel } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import {
  type ClipboardScorePolicy,
  type EmergencyCreditStatsCommon,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";

import { type AdjustmentsSectionItem } from "./Section";

export function getWorkingItems(policy: ClipboardScorePolicy): AdjustmentsSectionItem[] {
  const workShiftPoints = policy?.workShift?.points ?? 0;
  const workShiftItem: AdjustmentsSectionItem = {
    title: "Complete a shift",
    consequence: workShiftPoints,
  };

  return [
    ...(workShiftPoints > 0 ? [workShiftItem] : []),
    {
      title: "Abandon a shift",
      consequence: "Instant restriction",
    },
  ];
}

interface GetTimelinessItemsParams {
  policy: ClipboardScorePolicy;
  multiplier?: number;
  showCreditInfo?: boolean;
  hasCredit?: boolean;
}

export function getTimelinessItems(params: GetTimelinessItemsParams): AdjustmentsSectionItem[] {
  const { policy, multiplier, showCreditInfo = false, hasCredit } = params;

  const timelinessPoints = policy?.shiftClockInConfig?.points ?? 0;
  const timelinessItem = {
    title: "Arrive on time",
    consequence: timelinessPoints,
  };

  const latenessConfig = policy?.latenessConfig ?? [];
  const effectiveMultiplier = multiplier === 1 ? undefined : multiplier;

  const latenessItems = latenessConfig.map((config) => {
    const { points: consequence = 0, minutesLateRange, pointsWithEmergencyCredit } = config;
    const { startFrom, stopAt } = minutesLateRange;

    const range = isDefined(stopAt) ? `${startFrom}-${stopAt - 1}` : `${startFrom}+`;
    const title = `Arrive ${range} minutes late`;

    const baseItem = {
      title,
      consequence,
      multiplier: effectiveMultiplier,
    };

    if (showCreditInfo && isDefined(pointsWithEmergencyCredit)) {
      return {
        ...baseItem,
        hasCreditOption: true,
        pointsWithCredit: pointsWithEmergencyCredit,
        pointsWithoutCredit: consequence,
        hasCredit,
      };
    }

    return baseItem;
  });

  return [...(timelinessPoints > 0 ? [timelinessItem] : []), ...latenessItems].sort(
    (a, b) => b.consequence - a.consequence
  );
}

interface GetCancellationItemsParams {
  policy: ClipboardScorePolicy;
  multiplier?: number;
  showCreditInfo?: boolean;
  hasCredit?: boolean;
}

export function getCancellationItems(params: GetCancellationItemsParams): AdjustmentsSectionItem[] {
  const { policy, multiplier, showCreditInfo = false, hasCredit } = params;
  const cancellationConfig = policy?.cancelShift ?? [];
  const effectiveMultiplier = multiplier === 1 ? undefined : multiplier;

  return cancellationConfig
    .map((config) => {
      const { points: consequence = 0, leadTime, pointsWithEmergencyCredit } = config;
      const [fromOrNull, toOrNull] = leadTime;
      const from = fromOrNull ?? 0;
      const to = toOrNull ?? undefined;
      const rangeLabel = formatDurationRangeLabel(from, to, "hour", { showLiteralZero: true });
      const title =
        from === 0 && to === 0 ? "Cancel after shift start" : `Cancel with ${rangeLabel} notice`;

      const baseItem = {
        title,
        consequence,
        multiplier: effectiveMultiplier,
      };

      if (showCreditInfo && isDefined(pointsWithEmergencyCredit)) {
        return {
          ...baseItem,
          hasCreditOption: true,
          pointsWithCredit: pointsWithEmergencyCredit,
          pointsWithoutCredit: consequence,
          hasCredit,
        };
      }

      return baseItem;
    })
    .sort((a, b) => b.consequence - a.consequence);
}

export function getNoShowItems(): AdjustmentsSectionItem[] {
  return [
    {
      title: "No show",
      consequence: "Instant restriction",
    },
    {
      title: "Cancel after shift starts",
      consequence: "Instant restriction",
    },
  ];
}

interface CreditCalculations {
  shouldShowCreditInfo: boolean;
  hasCredit: boolean;
  shiftsUntilNextCredit: number;
}

export function calculateCreditInfo(
  creditStats: EmergencyCreditStatsCommon | undefined,
  isEnabled: boolean
): CreditCalculations {
  const shouldShowCreditInfo = isEnabled && isDefined(creditStats);
  const hasCredit = shouldShowCreditInfo && creditStats.totalAvailableCredits > 0;
  const shiftsUntilNextCredit = shouldShowCreditInfo
    ? creditStats.requiredShiftsForNextCredit - creditStats.completedShiftsTowardsNextCredit
    : 0;

  return { shouldShowCreditInfo, hasCredit, shiftsUntilNextCredit };
}
